import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../../utils/seo';

// ===== Professional Butler (EN) full modernization — Implementer B scope =====
// Design canon: src/pages/about/overview/page.tsx & home (navy #1a2942 / brass #a38e65 / gold #d7b779 / BTN-08 / no ornamental dividers).
// Factual canon (no fabrication, prices unchanged, EN = no Japanese characters):
//   [S-cur] prior JA src/pages/qualifications/professional-butler/page.tsx (12 chapters, ¥78,000, exam spec)
//   [S-overview] src/pages/about/overview/page.tsx (BTN08, section grammar)

const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

const curriculum = [
  { n: '01', t: 'Review and Development of Associate Butler', d: 'Consolidate the fundamentals and bridge to the Professional Butler level. Understand how prior learning translates into practice and prepare the mindset for more advanced study.' },
  { n: '02', t: 'Advanced Table Service', d: 'Flawless service for formal dinners. Master table settings, the sequence of serving a full-course meal, service flow design, and simultaneous attention to multiple guests.' },
  { n: '03', t: 'Advanced Hospitality Techniques', d: 'Service across varied situations. Refine guest reception, phone etiquette, guidance and escort, precise timing, and anticipatory preparation into truly professional technique.' },
  { n: '04', t: 'Estate Management in Practice', d: 'Comprehensive care of the living environment—from daily cleaning and periodic maintenance to the care of furniture and fine objects, garment management, and seasonal duties.' },
  { n: '05', t: 'VIP Service in Practice', d: 'High-quality service for discerning clients. Learn to anticipate needs, protect privacy and confidentiality, respond flexibly to special requests, and meet high expectations.' },
  { n: '06', t: 'Problem-Solving and Decision-Making', d: 'How to handle the unexpected. Prevent trouble, analyse and resolve problems, keep a calm judgment, set priorities, and offer alternatives with sound decision-making.' },
  { n: '07', t: 'Handling Complaints', d: 'Appropriate responses to complaints. Learn the fundamental stance, listening with empathy, composure, proposing solutions, and preventing recurrence to restore trust.' },
  { n: '08', t: 'Event and Party Management', d: 'From planning to execution. Master home-party planning, guest care, venue setup, run-of-show management, and team coordination for a successful occasion.' },
  { n: '09', t: 'Foundations of Leadership', d: 'The ability to lead a team. Learn the leader’s role, communication, directing and coordinating staff, motivation management, and building cooperation.' },
  { n: '10', t: 'Introduction to International Protocol', d: 'The basics of international etiquette—cultures and customs of various countries, hosting foreign guests, religious consideration, and a global perspective on service.' },
  { n: '11', t: 'Business Skills Fundamentals', d: 'Practical skills for carrying out duties—schedule management, document preparation, meeting arrangement, reporting, and business manners expected of a butler.' },
  { n: '12', t: 'Comprehensive Practical Exercise', d: 'Composite case studies and practical simulations. Integrate acquired knowledge and skills to confirm your capability—also important for exam preparation.' },
];

const recommended = [
  'Those who hold the Associate Butler certification',
  'Those wishing to provide high-quality service independently',
  'Those wanting to develop problem-solving and judgment',
  'Those wishing to demonstrate leadership',
  'Those seeking advancement at luxury hotels or private estates',
  'Those wishing to refine VIP-service capability',
  'Those aiming for the Master Butler certification',
];

const materials = [
  { t: 'Textbook (approx. 200 pages)', d: 'A dedicated textbook explaining advanced expertise in detail, rich with case studies to cultivate real-world judgment.' },
  { t: 'Online Video Lessons', d: 'Clear video explanations including practical skills. Demonstrations of advanced service technique—content only video can convey. One-year viewing period.' },
  { t: 'Practice Kit', d: 'Practice materials to support your learning, so you can rehearse advanced service techniques at home.' },
  { t: 'Certification Exam & Answer Sheet', d: 'Exam questions including practical case studies. Our association grades your returned submission.' },
];

const examRows = [
  ['Format', 'Written exam (essay, multiple choice, case study)'],
  ['Passing Score', '80 points or higher'],
  ['Deadline', 'Submit within 1 year of enrollment'],
  ['Eligibility', 'Associate Butler holders only'],
  ['Location', 'At home (distance exam)'],
  ['Retake', 'Unlimited retakes with a ¥15,000 retake fee'],
];

const ladder = [
  { key: 'associate', roman: 'I', name: 'Associate Butler', level: 'Beginner', price: '48,000', href: '/en/qualifications/associate-butler' },
  { key: 'professional', roman: 'II', name: 'Professional Butler', level: 'Intermediate', price: '78,000', href: '/en/qualifications/professional-butler' },
  { key: 'master', roman: 'III', name: 'Master Butler', level: 'Advanced', price: '128,000', href: '/en/qualifications/master-butler' },
];

function SecHead({ roman, en, children }: { roman: string; en: string; children: React.ReactNode }) {
  return (
    <div className="mb-9">
      <div className="-mx-5 sm:-mx-8 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 py-[18px]">
        <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">{roman}. {en}</span>
      </div>
      <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.45] tracking-[0.01em]">{children}</h2>
    </div>
  );
}

export default function EnProfessionalButlerPage() {
  useEffect(() => {
    updateSEO({
      title: 'Professional Butler Certification (Intermediate) | Japan Butler Association',
      description: 'The Professional Butler certification proves you can independently deliver high-quality service. Master VIP service, estate management, problem-solving, and leadership across 12 chapters. ¥78,000 for Associate Butler holders, with a complimentary 3-hour butler salon experience.',
      keywords: 'professional butler, butler certification, intermediate, VIP service, Japan Butler Association',
      canonical: 'https://butler-association.jp/en/qualifications/professional-butler',
    });
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Certifications', href: '/en/qualifications' },
    { label: 'Professional Butler' },
  ];

  return (
    <Layout>
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[540px] lg:min-h-[620px] flex items-center">
        <img
          src="/images/generated/jba-fs-en-034-qualifications_professional-butler.webp"
              alt="Close-up of a silver cloche handle used in formal service"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.74)_48%,rgba(16,20,26,0.36)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-5 sm:px-8 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Professional Butler · Level II</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="font-['Playfair_Display',serif] font-medium text-white text-[clamp(30px,3.6vw,46px)] leading-[1.25] tracking-[0.01em] mb-3">
              Stand on your own, <span className="text-[#d7b779]">Professional Butler</span>
            </h1>
            <p className="font-['Playfair_Display',serif] italic text-[#e7e2d8] text-sm tracking-[0.08em] mb-6">Intermediate Level</p>
            <div className="space-y-5 mb-8">
              <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-white/90 leading-[1.9]">
                Building on the Associate foundation, this intermediate certification adds the advanced expertise required in practice.
              </p>
              <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-white/90 leading-[1.9]">
                Develop the judgment to assess situations and choose the best response, the ability to solve complex problems, and the leadership to guide a team—everything needed to deliver high-quality service independently.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
              <div>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.2em] text-[10px] text-[#d7b779] mb-1">Fee</span>
                <span className="font-['Playfair_Display',serif] font-medium text-white text-[28px] leading-none">&yen;78,000</span>
                <span className="block font-['Playfair_Display',serif] text-[11px] text-white/60 mt-1">Tax &amp; shipping included</span>
              </div>
              <a href="/en/contact" className={BTN08_ON_NAVY}>Enroll Now</a>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb: placed right after the billboard hero (Yuichi 2026-07-17 note 2). Override shared Breadcrumb's header-clearance top padding via child selector */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-14 sm:py-18 lg:py-20 space-y-16 sm:space-y-20">

          <section>
            <SecHead roman="I" en="About This Certification">The applied power <span className="text-[#a38e65]">of practice</span></SecHead>
            <div className="space-y-[18px] font-['Playfair_Display',serif] text-base text-[#333] leading-[1.9] max-w-3xl">
              <div className="space-y-5">
                <p>The Professional Butler certification develops the fundamentals learned as an Associate into the advanced knowledge and technique required in real practice.</p>
                <p>Beyond executing tasks, you learn to assess situations and select the optimal response, solve complex problems, and lead other staff—the skills necessary for professional independence.</p>
              </div>
              <p>The curriculum makes extensive use of realistic case studies, cultivating not just theory but practical judgment across estate management, event operation, VIP service, and complaint handling.</p>
              <p>It is the core certification for those aiming to deliver high-quality service independently—and it opens the path to the highest tier, Master Butler.</p>
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              {[['Fee', '¥78,000'], ['Textbook', '~200 pp.'], ['Study', '4–6 mo.'], ['Prerequisite', 'Associate']].map(([k, v]) => (
                <div key={k}>
                  <dt className="font-['Playfair_Display',serif] uppercase tracking-[0.08em] text-[11px] text-[#8f7a56] mb-1.5">{k}</dt>
                  <dd className="font-['Playfair_Display',serif] font-medium text-2xl text-[#1a2942]">{v}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section>
            <SecHead roman="II" en="Why Professional">Three strengths <span className="text-[#a38e65]">the field demands</span></SecHead>
            <div className="grid md:grid-cols-3 gap-x-10 gap-y-8">
              {[
                { no: '01', ja: 'Advanced Skill', d: 'Master technique beyond the basics and rise to a level where you can deliver high-quality service independently.' },
                { no: '02', ja: 'Sound Judgment', d: 'Stay composed even in the unexpected. Analyse the situation and choose the optimal response with applied thinking.' },
                { no: '03', ja: 'Leadership', d: 'Bring a team together and guide other staff, raising the overall quality of service on site.' },
              ].map((f) => (
                <div key={f.no} className="border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none block mb-3">{f.no}</span>
                  <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-xl mb-2.5 leading-[1.4]">{f.ja}</h3>
                  <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">{f.d}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <SecHead roman="III" en="Curriculum · 12 Chapters">Twelve chapters of <span className="text-[#a38e65]">practical mastery</span></SecHead>
            <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.9] max-w-3xl mb-8">
              The Professional Butler certification covers practical service technique, judgment, and leadership across 12 areas. Built around case studies, it turns applied capability into something dependable.
            </p>
            <img
              src="/images/generated/jba-fs-en-035-qualifications_professional-butler.webp"
              alt="Professional Butler study materials with practical case studies"
              className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10"
            />
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              {curriculum.map((c) => (
                <div key={c.n} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{c.n}</span>
                  <div>
                    <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-lg mb-2.5 leading-[1.4]">{c.t}</h3>
                    <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">{c.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <SecHead roman="IV" en="Recommended For">This certification is <span className="text-[#a38e65]">ideal for</span></SecHead>
            <ul className="grid sm:grid-cols-2 gap-x-10 border-t-2 border-[#1a2942]">
              {recommended.map((r) => (
                <li key={r} className="flex items-start gap-3 border-b border-[#e2ddd2] py-4">
                  <span className="text-[#a38e65] text-[13px] leading-none pt-1.5" aria-hidden="true">◆</span>
                  <span className="font-['Playfair_Display',serif] text-[15px] text-[#1a2942] leading-[1.7]">{r}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <SecHead roman="V" en="Course Materials">Everything you need, <span className="text-[#a38e65]">delivered</span></SecHead>
            <div className="grid md:grid-cols-2 gap-6">
              {materials.map((m, i) => (
                <div key={m.t} className="flex gap-5 bg-[#f8f6f0] border border-[#a38e65]/25 p-6">
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl leading-none shrink-0 pt-0.5">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-base sm:text-lg mb-2 leading-[1.4]">{m.t}</h3>
                    <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">{m.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-[#1a2942] px-7 sm:px-10 py-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-7">
              <span className="font-['Playfair_Display',serif] italic text-[#d7b779] text-sm tracking-[0.06em] shrink-0">Special Bonus</span>
              <div>
                <h3 className="font-['Playfair_Display',serif] font-medium text-[#f8f6f0] text-lg mb-1">3-Hour Butler Salon Experience Ticket</h3>
                <p className="font-['Playfair_Display',serif] text-sm text-[#ded8cc] leading-[1.85]">A special ticket to experience real butler service at our Odaiba salon (¥30,000 value), deepening your advanced learning.</p>
              </div>
            </div>
          </section>

          <section>
            <SecHead roman="VI" en="Certification Exam">The exam is taken <span className="text-[#a38e65]">at home</span></SecHead>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <dl className="border-t-2 border-[#1a2942]">
                {examRows.map(([label, value]) => (
                  <div key={label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                    <dt className="sm:w-[120px] sm:shrink-0 font-['Playfair_Display',serif] uppercase tracking-[0.06em] text-[13px] text-[#8f7a56] pt-0.5">{label}</dt>
                    <dd className="flex-1 font-['Playfair_Display',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.8]">{value}</dd>
                  </div>
                ))}
              </dl>
              <div className="lg:pt-1">
                <img
                  src="/images/generated/jba-fs-175-qualifications_professional-butler-v7.webp"
              alt="Leather notebook and table clock suggesting study records and time management"
                  className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30 mb-4"
                />
                <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">
                  The Associate Butler certification is required to sit this exam. Take the written exam—including case studies—at home, return your answer sheet, and pass with 80 or higher to receive your certificate and card.
                </p>
              </div>
            </div>
          </section>

          <section>
            <SecHead roman="VII" en="The Three Tiers">Advance through <span className="text-[#a38e65]">three tiers</span></SecHead>
            <div className="space-y-5 mb-8">
              <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.9] max-w-3xl">
                Butler certification is earned step by step—from Associate Butler (Beginner) to Professional Butler (Intermediate) and Master Butler (Advanced). Level-skipping is not permitted.
              </p>
              <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.9] max-w-3xl">
                Beyond Professional lies Master Butler, the tier of leadership and oversight.
              </p>
            </div>
            <ol className="border-t border-[#e2ddd2]">
              {ladder.map((t) => {
                const current = t.key === 'professional';
                return (
                  <li key={t.key} className={current ? 'bg-[#1a2942] border-b border-[#e2ddd2]' : 'border-b border-[#e2ddd2]'}>
                    <a href={t.href} className={`flex flex-wrap items-center gap-x-6 gap-y-2 px-5 sm:px-7 py-5 group ${current ? '' : 'hover:bg-[#f8f6f0]'} transition-colors duration-300`}>
                      <span className={`font-['Playfair_Display',serif] text-2xl w-8 shrink-0 ${current ? 'text-[#d7b779]' : 'text-[#a38e65]'}`}>{t.roman}</span>
                      <div className="min-w-[220px]">
                        <span className={`block font-['Playfair_Display',serif] font-medium text-lg ${current ? 'text-[#f8f6f0]' : 'text-[#1a2942]'}`}>{t.name}{current && <span className="ml-3 align-middle text-[10px] tracking-[0.1em] text-[#1a2942] bg-[#d7b779] px-2 py-0.5">CURRENT PAGE</span>}</span>
                        <span className={`block font-['Playfair_Display',serif] italic text-xs mt-0.5 ${current ? 'text-[#c9c2b4]' : 'text-[#8f7a56]'}`}>{t.level}</span>
                      </div>
                      <span className={`font-['Playfair_Display',serif] font-medium text-xl ml-auto ${current ? 'text-[#d7b779]' : 'text-[#1a2942]'}`}>&yen;{t.price}</span>
                      <span className={`font-['Playfair_Display',serif] text-sm shrink-0 ${current ? 'text-[#d7b779]' : 'text-[#a38e65] group-hover:translate-x-1 transition-transform'}`}>{current ? '—' : 'View →'}</span>
                    </a>
                  </li>
                );
              })}
            </ol>
          </section>

          <section>
            <SecHead roman="VIII" en="Enrollment">Enrollment</SecHead>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <div className="flex flex-wrap items-end gap-x-8 gap-y-3 mb-8 pb-8 border-b border-white/15">
                <div>
                  <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.2em] text-[11px] text-[#d7b779] mb-1.5">Professional Butler · Fee</span>
                  <span className="font-['Playfair_Display',serif] font-medium text-white text-[34px] sm:text-[40px] leading-none">&yen;78,000</span>
                  <span className="block font-['Playfair_Display',serif] text-xs text-white/60 mt-1.5">Tax &amp; shipping included · No certification expiry</span>
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 flex-1 min-w-[260px]">
                  {['Textbook (approx. 200 pages)', 'Online video lessons (1 year)', 'Practice kit', 'Exam & answer sheet', 'Grading service', 'Certificate & ID card', '3-hour butler salon experience'].map((x) => (
                    <li key={x} className="flex items-start gap-2 font-['Playfair_Display',serif] text-[13px] text-[#e7e2d8] leading-[1.6]">
                      <span className="text-[#d7b779] text-[11px] pt-1" aria-hidden="true">◆</span>{x}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="font-['Playfair_Display',serif] text-base text-[#e7e2d8] leading-[1.9] max-w-3xl mb-8">
                Build the ability to stand on your own—steadily, through distance learning. Case studies cultivate real-world judgment and secure your path as an independent butler. Please feel free to contact us with any questions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/en/contact" className={BTN08_ON_NAVY}>Enroll or Ask a Question</a>
                <a href="/en/qualifications" className={BTN08_ON_NAVY}>Back to All Certifications</a>
              </div>
            </div>
            <div className="space-y-5 mt-6">
              <p className="font-['Playfair_Display',serif] text-xs text-[#8f7a56] leading-[1.85] max-w-3xl">
                This is a private certification issued by the Japan Butler Association. The Associate Butler certification is required to apply.
              </p>
              <p className="font-['Playfair_Display',serif] text-xs text-[#8f7a56] leading-[1.85] max-w-3xl">
                It does not guarantee employment, career advancement, or income; it is a learning programme for knowledge acquisition and self-improvement.
              </p>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
